/**
 * @file "My tasks — forms assigned to me" on Home: the user's most recently
 * modified assigned forms with FORM, PROJECT, STATE, VALUE and DUE. VALUE
 * stays in place reading "Not available yet", because a form's value lives
 * only in the analytics lake and Home makes no callable. DUE is relative and
 * red when late, and the State tag turns red with it. A state the form type
 * declares but `formStateDescription` does not list shows its raw key. A
 * click, Enter or Space on a row opens what the row names: the project detail
 * or the form.
 * Consumer: `HomePageContent`, which passes the read `useHomeMyTasks` returns.
 * Without `formsRead` or `formsWrite` the panel stays on screen, disabled
 * with a tooltip. The table scrolls inside a fixed height, and the card takes
 * its width from the row (`width: 0` under `flexGrow`), because a scrolling
 * table sized by its own content widens without bound. Below 600px
 * (`controlHeightLG * 15`) the table scrolls sideways inside the card. When
 * `ResponsiveTable` renders cards (a breakpoint below `md` positively reported)
 * they get no height cap, so the page scrolls rather than a box nested in it.
 */
import { FC } from 'react';
import { useNavigate } from 'react-router-dom';
import { Grid, Tag, Tooltip, Typography, theme } from 'antd';
import ResponsiveTable from '@/components/atoms/ResponsiveTable';
import type { TableColumnsType } from 'antd';

import { formStateDescription } from '#types';

import { AnalyticsCardTitle } from '@/components/analytics/AnalyticsCardTitle';

import FlexCard from '../../../atoms/FlexCard';
import type {
  HomeFormDue,
  HomeMyTask,
  HomeMyTasksRead,
} from '../../HomeMyTasks.types';

const { Text } = Typography;

const stateLabels: Partial<Record<string, { shortLabel: string }>> =
  formStateDescription;

const NO_RIGHT = 'My tasks needs the formsRead or formsWrite right.';

const TITLE = 'My tasks — forms assigned to me';

const UPPERCASE_HEADER = () => ({
  style: { textTransform: 'uppercase' as const },
});

const isLate = (due: HomeFormDue): boolean => due.status === 'set' && due.late;

const formLabel = ({ formNumber, formId }: HomeMyTask): string =>
  formNumber ?? formId;

const rowLabel = (task: HomeMyTask): string =>
  task.opens.target === 'project'
    ? `Open project ${task.project ?? 'detail'} from ${formLabel(task)}`
    : `Open form ${formLabel(task)}`;

const DueText: FC<{ due: HomeFormDue }> = ({ due }) => {
  switch (due.status) {
    case 'notConfigured':
      return <Text type="secondary">Not configured</Text>;
    case 'none':
      return <Text type="secondary">—</Text>;
    case 'set':
      return <Text type={due.late ? 'danger' : 'secondary'}>{due.text}</Text>;
  }
};

const FormNumber: FC<{ task: HomeMyTask }> = ({ task }) => {
  const { token } = theme.useToken();
  return (
    <Text type="secondary" style={{ fontFamily: token.fontFamilyCode }}>
      {formLabel(task)}
    </Text>
  );
};

const columns: TableColumnsType<HomeMyTask> = (
  [
    {
      key: 'form',
      title: 'Form',
      render: (_, task) => <FormNumber task={task} />,
    },
    {
      key: 'project',
      title: 'Project',
      render: (_, { project }) =>
        project === null ? (
          <Text type="secondary">—</Text>
        ) : (
          <Text>{project}</Text>
        ),
    },
    {
      key: 'state',
      title: 'State',
      render: (_, { state, due }) =>
        state === null ? (
          <Text type="secondary">—</Text>
        ) : (
          <Tag color={isLate(due) ? 'error' : undefined}>
            {stateLabels[state]?.shortLabel ?? state}
          </Tag>
        ),
    },
    {
      key: 'value',
      title: 'Value',
      align: 'right',
      render: () => <Text type="secondary">Not available yet</Text>,
    },
    {
      key: 'due',
      title: 'Due',
      render: (_, { due }) => <DueText due={due} />,
    },
  ] satisfies TableColumnsType<HomeMyTask>
).map((column) => ({ ...column, onHeaderCell: UPPERCASE_HEADER }));

export const HomeMyTasks: FC<{ read: HomeMyTasksRead }> = ({ read }) => {
  const { token } = theme.useToken();
  const { xs, sm, md } = Grid.useBreakpoint();
  const cardMode = !md && Boolean(xs || sm);
  const navigate = useNavigate();
  const isDisabled = read.status === 'noRight';

  return (
    <Tooltip title={isDisabled ? NO_RIGHT : undefined}>
      <FlexCard
        growCard
        style={{ width: 0 }}
        role="region"
        aria-label={TITLE}
        aria-disabled={isDisabled ? 'true' : undefined}
      >
        <AnalyticsCardTitle title={TITLE} />
        {read.status === 'failed' && <Text type="danger">{read.message}</Text>}
        <ResponsiveTable<HomeMyTask>
          aria-label="My tasks"
          size="small"
          rowKey="formId"
          pagination={false}
          scroll={{
            x: token.controlHeightLG * 15,
            y: cardMode ? undefined : token.controlHeightLG * 5,
          }}
          loading={read.status === 'loading'}
          columns={columns}
          dataSource={read.status === 'found' ? [...read.tasks] : []}
          locale={{
            emptyText: isDisabled ? NO_RIGHT : 'No forms are assigned to you.',
          }}
          onRow={(task) => ({
            tabIndex: 0,
            'aria-label': rowLabel(task),
            style: { cursor: 'pointer' },
            onClick: () => void navigate(task.opens.to),
            onKeyDown: (event) => {
              if (event.key !== 'Enter' && event.key !== ' ') return;
              event.preventDefault();
              void navigate(task.opens.to);
            },
          })}
        />
      </FlexCard>
    </Tooltip>
  );
};
